'use strict';

var update = require('react/lib/update'),
    _ = require('underscore'),
    vendorPrefix = require('../utils/vendorPrefix'),
    { isMobile, isIE10 } = require('../utils/Browser'),
    LightboxActionCreators = require('../actions/LightboxActionCreators'),
    KeyCodes = require('../constants/KeyCodes');

// Keep this synced with zindexes.less
var LIGHTBOX_IMAGE_Z = 250;

/**
 * Manages lightbox state for a list of images.
 *
 * Provides methods to find out if image is currently in the lightbox,
 * and what styles it needs in order to become visible in the viewport.
 *
 * Call `handleLightboxTransitionEnd` whenever lightbox transition ends.
 *
 * An easy way to do that would be to use `Image`s in place of `img`s
 * and set `onTransitionEnd={this.handleLightboxTransitionEnd}` on them.
 */
var LightboxMixin = {
  getInitialState: function () {
    return this.getInitialLightboxState();
  },

  getInitialLightboxState: function () {
    return {
      lightbox: {
        current: null,
        previous: null
      }
    };
  },

  componentDidUpdate: function (prevProps, prevState) {
    var previousLightbox = prevState.lightbox.current,
        hasChanged = !_.isEqual(previousLightbox, this.state.lightbox.current);

    if (!hasChanged) {
      return;
    }

    if (this.isInLightbox()) {
      window.addEventListener('scroll', this.hideLightbox, false);
      window.addEventListener('resize', this.hideLightbox, false);
      window.addEventListener('keydown', this._handleEscapeKeyDown, false);
    } else {
      window.removeEventListener('scroll', this.hideLightbox, false);
      window.removeEventListener('resize', this.hideLightbox, false);
      window.removeEventListener('keydown', this._handleEscapeKeyDown, false);
    }
  },

  componentWillUnmount: function () {
    window.removeEventListener('scroll', this.hideLightbox, false);
    window.removeEventListener('resize', this.hideLightbox, false);
    window.removeEventListener('keydown', this._handleEscapeKeyDown, false);

    if (this.isLightboxVisible()) {
      LightboxActionCreators.hideLightbox();
    }
  },

  /**
   * Restores the lightbox to its original state.
   */
  resetLightbox: function () {
    this.setState(this.getInitialLightboxState());

    if (this.isLightboxVisible()) {
      LightboxActionCreators.hideLightbox();
    }
  },

  /**
   * Call this method to let lightbox know the transition is over.
   *
   * An easy way to do that would be to use `Image`s in place of `img`s
   * and set `onTransitionEnd={this.handleLightboxTransitionEnd}` on them.
   */
  handleLightboxTransitionEnd: function () {
    this._setLightboxState('previous', null);
  },

  /**
   * Closes lightbox on Escape button.
   */
  _handleEscapeKeyDown: function (e) {
    if (e.keyCode === KeyCodes.ESCAPE) {
      this.hideLightbox();
    }
  },

  /**
   * Returns 'active' if lightbox is active, 'leaving' if it is being animated away, or false.
   *
   * @param {Number?} sectionIndex Optionally, ask about specific section.
   * @returns {String?}
   */
  getLightboxState: function (sectionIndex) {
    if (this.isInLightbox(sectionIndex)) {
      return 'active';
    }

    if (this.isLeavingLightbox(sectionIndex)) {
      return 'leaving';
    }
  },

  /**
   * Returns true if lightbox is visible, false otherwise.
   *
   * @param {Number?} sectionIndex Optionally, ask about specific section.
   * @returns {Boolean}
   */
  isLightboxVisible: function (sectionIndex) {
    return !!this.getLightboxState(sectionIndex);
  },

  /**
   * Returns whether lightbox is currently active.
   *
   * @param {Number?} sectionIndex Optionally, ask about specific section.
   * @returns {Boolean}
   */
  isInLightbox: function (sectionIndex) {
    var current = this.state.lightbox.current;
    if (!current) {
      return false;
    }

    return _.isUndefined(sectionIndex) ||
      current.sectionIndex === sectionIndex;
  },

  /**
   * Returns whether lightbox is currently being animated away.
   *
   * @param {Number?} sectionIndex Optionally, ask about specific section.
   * @returns {Boolean}
   */
  isLeavingLightbox: function (sectionIndex) {
    var previous = this.state.lightbox.previous;
    if (!previous) {
      return false;
    }

    return _.isUndefined(sectionIndex) ||
      previous.sectionIndex === sectionIndex;
  },

  /**
   * Returns whether a given image is displayed in lightbox.
   *
   * @param {ImageMedia} image
   */
  isImageInLightbox: function (image) {
    return this.isInLightbox() &&
           this.state.lightbox.current.image.uuid === image.uuid;
  },

  /**
   * Returns whether image with given index is being animated away from the lightbox.
   *
   * @param {ImageMedia} image
   */
  isImageLeavingLightbox: function (image) {
    return this.isLeavingLightbox() &&
           this.state.lightbox.previous.image.uuid === image.uuid;
  },

  /**
   * If lightbox is active, closes it.
   * Otherwise, shows lightbox for a given image.
   *
   * Provide `fromRect` so `getLightboxImageStyle` knows
   * how to calculate the transform that animates image into view.
   *
   * @param {number} sectionIndex Section index.
   * @param {SectionType} sectionType Section type.
   * @param {ImageMedia} image Image, later to be provided in `getLightboxImageStyle`.
   * @param {object} fromRect Current image position on the page.
   * @param {number?} toHeight Target container height. Window height if unspecified.
   */
  toggleLightbox: function (sectionIndex, sectionType, image, fromRect, toHeight) {
    if (this.isInLightbox()) {
      this.hideLightbox();
    } else {
      this.showLightbox(sectionIndex, sectionType, image, fromRect, toHeight);
    }
  },

  isLightboxEnabled: function () {
    return !isMobile() && !isIE10() && window.matchMedia('(min-width: 992px)').matches;
  },

  /**
   * Shows lightbox for a given image.
   *
   * Provide `fromRect` so `getLightboxImageStyle` knows
   * how to calculate the transform that animates image into view.
   *
   * @param {number} sectionIndex Section index.
   * @param {SectionType} sectionType Section type.
   * @param {ImageMedia} image Image later to be queried via `getLightboxImageStyle`.
   * @param {object} fromRect Current image position on the page.
   * @param {number?} toHeight Target container height. Window height if unspecified.
   */
  showLightbox: function (sectionIndex, sectionType, image, fromRect, toHeight) {
    if (!this.isLightboxEnabled()) {
      return;
    }

    this._setLightboxState('current', {
      sectionIndex: sectionIndex,
      sectionType: sectionType,
      image: image,
      fromRect: fromRect,
      toHeight: toHeight || window.innerHeight
    });

    LightboxActionCreators.showLightbox(image, sectionType);
  },

  /**
   * Hides lightbox.
   */
  hideLightbox: function () {
    if (!this.isInLightbox()) {
      return;
    }

    this._setLightboxState('current', null);

    LightboxActionCreators.hideLightbox();
  },

  /**
   * Gets CSS style that needs to be applied to
   * any image in lightbox-enabled gallery, whether
   * the image is active or not.
   *
   * @param {ImageMedia} image Image previously provided in `showLightbox`.
   */
  getLightboxImageStyle: function (image) {
    var transform = this.getLightboxImageTransform(image),
        style = {};

    style[vendorPrefix.js + 'Transform'] = transform;

    if (this.isImageInLightbox(image) || this.isImageLeavingLightbox(image)) {
      style.position = 'relative';
      style.zIndex = LIGHTBOX_IMAGE_Z;
    }

    return style;
  },

  /**
   * Gets CSS transform that needs to be applied to
   * any image in lightbox-enabled gallery, whether
   * the image is active or not.
   *
   * Assumes that when lightbox is active, `state.lightbox.current.fromRect`
   * matches image initial position just before lightbox was activated.
   *
   * @param {ImageMedia} image Image previously provided in `showLightbox`.
   */
  getLightboxImageTransform: function (image) {
    if (!this.isImageInLightbox(image)) {
      return 'translate(0, 0) scale(1)';
    }

    var origSize = image.size,
        { fromRect, toHeight } = this.state.lightbox.current,
        toWidth = window.innerWidth,
        leftGap = 16,
        rightGap = 16,
        topGap = 16,
        bottomGap = 68,
        availableWidth = toWidth - (leftGap + rightGap),
        availableHeight = toHeight - (topGap + bottomGap),
        widthRatio = origSize.width / fromRect.width,
        scale;

    if (origSize.width < availableWidth && origSize.height < availableHeight) {
      scale = widthRatio;
    } else if (origSize.width / origSize.height < availableWidth / availableHeight) {
      scale = availableHeight / origSize.height * widthRatio;
    } else {
      scale = availableWidth / origSize.width * widthRatio;
    }

    var horizontalGapAdjustment = (rightGap - leftGap) / 2,
        verticalGapAdjustment = (topGap - bottomGap) / 2,
        left = (toWidth - fromRect.width) / 2 - fromRect.left + horizontalGapAdjustment,
        top = (toHeight - fromRect.height) / 2 - fromRect.top + verticalGapAdjustment,
        transform = 'translate(' + left + 'px, ' + top + 'px) scale(' + scale + ')';

    return transform;
  },

  _setLightboxState: function (key, value) {
    var stateUpdate = {};

    stateUpdate[key] = {
      $set: value
    };

    if (key === 'current' && value === null) {
      var current = this.state.lightbox.current;
      stateUpdate['previous'] = {
        $set: current
      };
    }

    this._updateState({
      lightbox: stateUpdate
    });
  },

  _updateState: function (stateUpdate) {
    if (!this.isMounted()) {
      return;
    }

    this.replaceState(update(this.state, stateUpdate));
  }
};

module.exports = LightboxMixin;



/** WEBPACK FOOTER **
 ** ./mixins/LightboxMixin.js
 **/